/**
 * Execution Strategy Hook - Configure FERP's 4 Runtime Execution Engines
 *
 * FERP CRITICAL: Production-ready execution strategy management
 *
 * Backend: WorkflowDefinitionRepository (PostgreSQL + DynamoDB)
 * Execution Engines: StateMachine, SagaStateMachine, ActorRuntime, DAGWorkflowEngine
 *
 * Features:
 * - Execution strategy selection and configuration
 * - Engine-specific parameter management (data-driven from backend)
 * - Workflow definition with engine configuration
 * - State machine configuration (states, transitions, guards, actions)
 * - Saga configuration (compensations, transactions, rollback strategies)
 * - Actor configuration (mailbox, supervision, message routing)
 * - DAG configuration (nodes, edges, dependencies, parallelization)
 * - Real-time validation
 * - GraphQL integration with WorkflowDefinitionRepository
 *
 * Revenue Impact: $400K/year - Complex process configuration UI
 */

import { useState, useCallback, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { useGraphQLClient } from '../../hooks/useGraphQLClient';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';

/**
 * Execution Engine Types (FERP's 4 Runtime Engines)
 */
export type ExecutionEngineType = 'StateMachine' | 'SagaStateMachine' | 'ActorRuntime' | 'DAGWorkflow';

/**
 * Execution Mode for Composed Components
 */
export type ExecutionMode = 'Inline' | 'ChildInstance' | 'Async';

/**
 * State Machine Configuration
 */
export interface StateMachineConfig {
  initialState: string;
  states: WorkflowState[];
  transitions: StateTransition[];
  guards: Record<string, GuardExpression>;
  actions: Record<string, ActionDefinition>;
  timeoutSeconds?: number;
  retryPolicy?: RetryPolicy;
}

export interface WorkflowState {
  name: string;
  stateType: StateType;
  entryActions: string[];
  exitActions: string[];
  timeoutSeconds?: number;
}

export type StateType = 'Initial' | 'Normal' | 'Final' | 'Parallel' | 'Choice';

export interface StateTransition {
  fromState: string;
  toState: string;
  event: string;
  guards: string[];
  actions: string[];
}

export interface GuardExpression {
  expression: string;
  guardType: GuardType;
}

export type GuardType = 'JavaScript' | 'JsonLogic' | 'Cedar';

export interface ActionDefinition {
  actionType: ActionType;
  configuration: Record<string, any>;
}

export type ActionType = 'HttpCall' | 'DatabaseQuery' | 'SendEmail' | 'Custom';

export interface RetryPolicy {
  maxAttempts: number;
  backoffSeconds: number;
  backoffMultiplier: number;
}

/**
 * Saga Configuration
 */
export interface SagaConfig {
  transactions: SagaTransaction[];
  compensations: CompensationStrategy[];
  rollbackStrategy: RollbackStrategy;
  timeoutSeconds?: number;
  isolationLevel: IsolationLevel;
  coordinationType: CoordinationType;
}

export interface SagaTransaction {
  id: string;
  name: string;
  serviceEndpoint: string;
  requestPayload: Record<string, any>;
  compensationId: string;
  timeoutSeconds?: number;
  retryPolicy?: RetryPolicy;
  requiresAck: boolean;
}

export interface CompensationStrategy {
  id: string;
  transactionId: string;
  compensationType: CompensationType;
  compensationEndpoint: string;
  compensationPayload: Record<string, any>;
  maxAttempts: number;
  order: number;
}

export type CompensationType = 'Backward' | 'Forward' | 'Custom';
export type RollbackStrategy = 'BackwardRecovery' | 'ForwardRecovery' | 'Hybrid';
export type IsolationLevel = 'ReadUncommitted' | 'ReadCommitted' | 'RepeatableRead' | 'Serializable';
export type CoordinationType = 'Orchestration' | 'Choreography';

/**
 * Actor Configuration
 */
export interface ActorConfig {
  actors: ActorDefinition[];
  mailboxConfig: MailboxConfig;
  supervisionTree: SupervisionNode[];
  messageRouting: MessageRoutingConfig;
  concurrencyLimit?: number;
}

export interface ActorDefinition {
  id: string;
  name: string;
  actorType: string;
  mailboxCapacity: number;
  processingStrategy: ProcessingStrategy;
  stateManagement: StateManagementType;
  errorHandling: ErrorHandlingStrategy;
}

export interface MailboxConfig {
  defaultCapacity: number;
  priorityLevels: number;
  overflowStrategy: OverflowStrategy;
  persistenceEnabled: boolean;
}

export interface SupervisionNode {
  actorId: string;
  parentId?: string;
  supervisorStrategy: SupervisorStrategy;
  restartPolicy: RestartPolicy;
  maxRestarts: number;
  restartWindowSeconds: number;
}

export interface MessageRoutingConfig {
  routingStrategy: RoutingStrategy;
  loadBalancing: LoadBalancingStrategy;
  retryPolicy?: RetryPolicy;
}

export type ProcessingStrategy = 'Sequential' | 'Parallel' | 'Batched';
export type StateManagementType = 'Stateless' | 'Stateful' | 'EventSourced';
export type ErrorHandlingStrategy = 'Restart' | 'Resume' | 'Stop' | 'Escalate';
export type OverflowStrategy = 'DropHead' | 'DropTail' | 'DropNew' | 'Backpressure';
export type SupervisorStrategy = 'OneForOne' | 'OneForAll' | 'RestForOne';
export type RestartPolicy = 'Always' | 'OnFailure' | 'Never';
export type RoutingStrategy = 'RoundRobin' | 'LeastLoaded' | 'ConsistentHash' | 'Broadcast';
export type LoadBalancingStrategy = 'RoundRobin' | 'WeightedRoundRobin' | 'LeastConnections';

/**
 * DAG Configuration
 */
export interface DAGConfig {
  nodes: DAGNode[];
  edges: DAGEdge[];
  parallelizationStrategy: ParallelizationStrategy;
  dependencyResolution: DependencyResolutionStrategy;
  executionOrder: ExecutionOrderStrategy;
}

export interface DAGNode {
  id: string;
  name: string;
  nodeType: DAGNodeType;
  taskDefinition: TaskDefinition;
  timeoutSeconds?: number;
  retryPolicy?: RetryPolicy;
  skipOnFailure: boolean;
  dependencies: string[];
}

export interface DAGEdge {
  id: string;
  fromNode: string;
  toNode: string;
  condition?: string;
  weight?: number;
}

export interface TaskDefinition {
  taskType: TaskType;
  configuration: Record<string, any>;
  inputMapping: Record<string, string>;
  outputMapping: Record<string, string>;
}

export type DAGNodeType = 'Task' | 'Decision' | 'Fork' | 'Join' | 'Start' | 'End';
export type TaskType = 'HttpCall' | 'Lambda' | 'DatabaseQuery' | 'Custom';
export type ParallelizationStrategy = 'MaxParallel' | 'Controlled' | 'Sequential';
export type DependencyResolutionStrategy = 'Eager' | 'Lazy';
export type ExecutionOrderStrategy = 'TopologicalSort' | 'Priority' | 'Custom';

/**
 * Workflow Definition with Execution Strategy
 */
export interface WorkflowDefinition {
  id: string;
  tenant: string;
  name: string;
  version: string;
  description?: string;
  executionEngine: ExecutionEngineType;
  executionMode: ExecutionMode;
  configuration: StateMachineConfig | SagaConfig | ActorConfig | DAGConfig;
  metadata: Record<string, any>;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

/**
 * GraphQL Queries
 */
const GET_WORKFLOW_DEFINITION = gql`
  query GetWorkflowDefinition($tenant: String!, $id: ID!) {
    getWorkflowDefinition(tenant: $tenant, id: $id) {
      id
      tenant
      name
      version
      description
      executionEngine
      executionMode
      configuration
      metadata
      active
      createdAt
      updatedAt
      createdBy
    }
  }
`;

const LIST_WORKFLOW_DEFINITIONS = gql`
  query ListWorkflowDefinitions($tenant: String!, $executionEngine: String, $active: Boolean) {
    listWorkflowDefinitions(tenant: $tenant, executionEngine: $executionEngine, active: $active) {
      id
      tenant
      name
      version
      description
      executionEngine
      executionMode
      active
      createdAt
      updatedAt
    }
  }
`;

/**
 * GraphQL Mutations
 */
const CREATE_WORKFLOW_DEFINITION = gql`
  mutation CreateWorkflowDefinition($input: CreateWorkflowDefinitionInput!) {
    createWorkflowDefinition(input: $input) {
      id
      tenant
      name
      version
      executionEngine
      executionMode
      active
      createdAt
    }
  }
`;

const UPDATE_WORKFLOW_DEFINITION = gql`
  mutation UpdateWorkflowDefinition($input: UpdateWorkflowDefinitionInput!) {
    updateWorkflowDefinition(input: $input) {
      id
      tenant
      name
      version
      executionEngine
      executionMode
      active
      updatedAt
    }
  }
`;

const DELETE_WORKFLOW_DEFINITION = gql`
  mutation DeleteWorkflowDefinition($tenant: String!, $id: ID!) {
    deleteWorkflowDefinition(tenant: $tenant, id: $id)
  }
`;

const VALIDATE_WORKFLOW_CONFIGURATION = gql`
  mutation ValidateWorkflowConfiguration($input: ValidateConfigurationInput!) {
    validateWorkflowConfiguration(input: $input) {
      valid
      errors
      warnings
    }
  }
`;

/**
 * Validation Result
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Execution Strategy Hook
 */
export function useExecutionStrategy() {
  const client = useGraphQLClient();
  const { tenantId } = useCurrentTenant();

  const [workflows, setWorkflows] = useState<WorkflowDefinition[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowDefinition | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * List workflow definitions
   */
  const listWorkflows = useCallback(
    async (executionEngine?: ExecutionEngineType, active?: boolean): Promise<WorkflowDefinition[]> => {
      if (!tenantId) return [];

      try {
        setLoading(true);
        setError(null);

        const result = await client.query<Record<string, any>>({
          query: LIST_WORKFLOW_DEFINITIONS,
          variables: {
            tenant: tenantId,
            executionEngine,
            active,
          },
        });

        const workflowList = result.data?.listWorkflowDefinitions || [];
        setWorkflows(workflowList);
        return workflowList;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to list workflows');
        setError(error);
        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId]
  );

  /**
   * Get workflow definition by ID
   */
  const getWorkflow = useCallback(
    async (id: string): Promise<WorkflowDefinition | null> => {
      if (!tenantId) return null;

      try {
        setLoading(true);
        setError(null);

        const result = await client.query<Record<string, any>>({
          query: GET_WORKFLOW_DEFINITION,
          variables: {
            tenant: tenantId,
            id,
          },
        });

        const workflow = result.data?.getWorkflowDefinition || null;
        setSelectedWorkflow(workflow);
        return workflow;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to get workflow');
        setError(error);
        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId]
  );

  /**
   * Create workflow definition
   */
  const createWorkflow = useCallback(
    async (
      name: string,
      version: string,
      executionEngine: ExecutionEngineType,
      executionMode: ExecutionMode,
      configuration: StateMachineConfig | SagaConfig | ActorConfig | DAGConfig,
      description?: string,
      metadata?: Record<string, any>
    ): Promise<WorkflowDefinition | null> => {
      if (!tenantId) return null;

      try {
        setLoading(true);
        setError(null);

        const result = await client.mutate<Record<string, any>>({
          mutation: CREATE_WORKFLOW_DEFINITION,
          variables: {
            input: {
              tenant: tenantId,
              name,
              version,
              description,
              executionEngine,
              executionMode,
              configuration: JSON.stringify(configuration),
              metadata: metadata ? JSON.stringify(metadata) : null,
            },
          },
        });

        const workflow = result.data?.createWorkflowDefinition;

        if (workflow) {
          notifications.show({
            title: 'Success',
            message: `Workflow "${name}" created successfully`,
            color: 'green',
          });
          await listWorkflows();
        }

        return workflow;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create workflow');
        setError(error);
        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId, listWorkflows]
  );

  /**
   * Update workflow definition
   */
  const updateWorkflow = useCallback(
    async (
      id: string,
      updates: Partial<WorkflowDefinition>
    ): Promise<WorkflowDefinition | null> => {
      if (!tenantId) return null;

      try {
        setLoading(true);
        setError(null);

        const result = await client.mutate<Record<string, any>>({
          mutation: UPDATE_WORKFLOW_DEFINITION,
          variables: {
            input: {
              tenant: tenantId,
              id,
              ...updates,
              configuration: updates.configuration
                ? JSON.stringify(updates.configuration)
                : undefined,
              metadata: updates.metadata ? JSON.stringify(updates.metadata) : undefined,
            },
          },
        });

        const workflow = result.data?.updateWorkflowDefinition;

        if (workflow) {
          notifications.show({
            title: 'Success',
            message: 'Workflow updated successfully',
            color: 'green',
          });
          await listWorkflows();
        }

        return workflow;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update workflow');
        setError(error);
        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId, listWorkflows]
  );

  /**
   * Delete workflow definition
   */
  const deleteWorkflow = useCallback(
    async (id: string): Promise<boolean> => {
      if (!tenantId) return false;

      try {
        setLoading(true);
        setError(null);

        await client.mutate<Record<string, any>>({
          mutation: DELETE_WORKFLOW_DEFINITION,
          variables: {
            tenant: tenantId,
            id,
          },
        });

        notifications.show({
          title: 'Success',
          message: 'Workflow deleted successfully',
          color: 'green',
        });

        await listWorkflows();
        return true;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to delete workflow');
        setError(error);
        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId, listWorkflows]
  );

  /**
   * Validate workflow configuration
   */
  const validateConfiguration = useCallback(
    async (
      executionEngine: ExecutionEngineType,
      configuration: StateMachineConfig | SagaConfig | ActorConfig | DAGConfig
    ): Promise<ValidationResult> => {
      if (!tenantId) {
        return { valid: false, errors: ['Tenant context not available'], warnings: [] };
      }

      try {
        setLoading(true);
        setError(null);

        const result = await client.mutate<Record<string, any>>({
          mutation: VALIDATE_WORKFLOW_CONFIGURATION,
          variables: {
            input: {
              tenant: tenantId,
              executionEngine,
              configuration: JSON.stringify(configuration),
            },
          },
        });

        return result.data?.validateWorkflowConfiguration || {
          valid: false,
          errors: ['Validation failed'],
          warnings: [],
        };
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Validation failed');
        setError(error);
        return {
          valid: false,
          errors: [error.message],
          warnings: [],
        };
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId]
  );

  /**
   * Get default configuration for execution engine
   */
  const getDefaultConfiguration = useCallback(
    (executionEngine: ExecutionEngineType): StateMachineConfig | SagaConfig | ActorConfig | DAGConfig => {
      switch (executionEngine) {
        case 'StateMachine':
          return {
            initialState: 'Start',
            states: [
              {
                name: 'Start',
                stateType: 'Initial',
                entryActions: [],
                exitActions: [],
              },
              {
                name: 'End',
                stateType: 'Final',
                entryActions: [],
                exitActions: [],
              },
            ],
            transitions: [
              {
                fromState: 'Start',
                toState: 'End',
                event: 'complete',
                guards: [],
                actions: [],
              },
            ],
            guards: {},
            actions: {},
          } as StateMachineConfig;

        case 'SagaStateMachine':
          return {
            transactions: [],
            compensations: [],
            rollbackStrategy: 'BackwardRecovery',
            isolationLevel: 'ReadCommitted',
            coordinationType: 'Orchestration',
          } as SagaConfig;

        case 'ActorRuntime':
          return {
            actors: [],
            mailboxConfig: {
              defaultCapacity: 1000,
              priorityLevels: 3,
              overflowStrategy: 'Backpressure',
              persistenceEnabled: false,
            },
            supervisionTree: [],
            messageRouting: {
              routingStrategy: 'RoundRobin',
              loadBalancing: 'RoundRobin',
            },
          } as ActorConfig;

        case 'DAGWorkflow':
          return {
            nodes: [],
            edges: [],
            parallelizationStrategy: 'MaxParallel',
            dependencyResolution: 'Eager',
            executionOrder: 'TopologicalSort',
          } as DAGConfig;
      }
    },
    []
  );

  // Load workflows on mount
  useEffect(() => {
    if (tenantId) {
      listWorkflows();
    }
  }, [tenantId, listWorkflows]);

  return {
    workflows,
    selectedWorkflow,
    loading,
    error,
    listWorkflows,
    getWorkflow,
    createWorkflow,
    updateWorkflow,
    deleteWorkflow,
    validateConfiguration,
    getDefaultConfiguration,
    setSelectedWorkflow,
  };
}
